import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { environment } from '../environments/environment';
import { PaymentProvider, PaymentResult } from './interfaces/payment-provider';
import { resource } from '../utils/resource/old';

declare var Stripe: any;

@Injectable({
  providedIn: 'root'
})
export class DwspayService implements PaymentProvider {
  private stripe: any = null;
  private elements: any = null;
  private paymentElement: any = null;
  private paymentElementReadySubject: Subject<boolean> = new Subject<boolean>();
  private paymentElementChangeSubject: Subject<any> = new Subject<any>();
  private isPaymentElementValid: boolean = false;
  private storedClientSecret: string | null = null;

  constructor() {
  }

  /**
   * Initialize Stripe instance with platform account (not winery account)
   * Implements PaymentProvider.initializePayment
   */
  public async initializePayment(): Promise<void> {
    return this.initStripe();
  }

  /**
   * Initialize Stripe instance with platform account (not winery account)
   */
  public async initStripe(): Promise<void> {
    if (this.stripe) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      this.injectIntoHead().then(() => {
        let opts: any = {};
        
        // Set locale if needed (can be configured based on app language)
        // For now, default to 'en'
        opts.locale = 'en';

        // Use DWSPay publishable key (WS-Payments platform account)
        const dwspayPublishKey = environment.values.dwspayPublishKey;
        if (!dwspayPublishKey) {
          reject(new Error('DWSPay publishable key is not configured'));
          return;
        }

        this.stripe = Stripe(dwspayPublishKey, opts);
        // Don't create elements here - they need to be created with clientSecret in initPaymentElement
        resolve();
      }).catch(reject);
    });
  }

  /**
   * Create Payment Element - implements PaymentProvider.createPaymentElement
   * Note: For Payment Element, clientSecret is required. This method will use stored clientSecret
   * if available, otherwise it will return the existing payment element if already initialized.
   * For proper initialization, call initPaymentElement(clientSecret) directly.
   */
  public async createPaymentElement(): Promise<any> {
    // If payment element is already initialized, return it
    if (this.paymentElement) {
      return this.paymentElement;
    }
    
    // If we have a stored clientSecret, initialize with it
    if (this.storedClientSecret) {
      await this.initPaymentElement(this.storedClientSecret, 'dwspay-payment-element');
      return this.paymentElement;
    }
    
    // Otherwise, throw error as we need clientSecret for Payment Element
    throw new Error('Client secret not available. For dwspay Payment Element, call initPaymentElement(clientSecret) first.');
  }

  /**
   * Initialize Payment Element with client secret and mount to container
   * This method stores the clientSecret for use by createPaymentElement()
   */
  public async initPaymentElement(clientSecret: string, elementContainerId: string = 'dwspay-payment-element'): Promise<void> {
    // Store clientSecret for PaymentProvider interface compatibility
    this.storedClientSecret = clientSecret;
    if (!this.stripe) {
      await this.initStripe();
    }

    // Create Elements group with clientSecret (required for Payment Element)
    // If elements already exists, destroy it first to recreate with new clientSecret
    if (this.elements) {
      this.elements = null;
    }
    
    this.elements = this.stripe.elements({
      clientSecret: clientSecret
    });

    // Create Payment Element
    const appearance = {
      theme: 'stripe' as const,
    };

    this.paymentElement = this.elements.create('payment', {
      appearance: appearance
    });

    // Mount the Payment Element
    const container = document.getElementById(elementContainerId);
    if (!container) {
      throw new Error(`Container element with id "${elementContainerId}" not found`);
    }

    this.paymentElement.mount(`#${elementContainerId}`);

    // Listen for ready event
    this.paymentElement.on('ready', () => {
      this.paymentElementReadySubject.next(true);
    });

    // Listen for change events (validation)
    this.paymentElement.on('change', (event: any) => {
      this.isPaymentElementValid = event.complete && !event.error;
      this.paymentElementChangeSubject.next(event);
    });
  }

  /**
   * Get Payment Element instance
   */
  public getPaymentElement(): any {
    return this.paymentElement;
  }

  /**
   * Submit Elements form - validates the form and collects payment details
   * MUST be called before confirmPayment() according to Stripe's deferred payment flow
   */
  public async submitElements(): Promise<any> {
    console.log('[DwspayService] submitElements called');
    
    if (!this.elements) {
      console.error('[DwspayService] Elements not initialized');
      throw new Error('Stripe Elements not initialized. Call initPaymentElement() first.');
    }

    if (!this.paymentElement) {
      console.error('[DwspayService] Payment Element not initialized');
      throw new Error('Payment Element not initialized. Call initPaymentElement() first.');
    }

    console.log('[DwspayService] Calling elements.submit() to validate form and collect payment details...');
    try {
      const submitResult = await this.elements.submit();
      
      if (submitResult.error) {
        console.error('[DwspayService] elements.submit() returned error:', submitResult.error);
        return { error: submitResult.error };
      } else {
        console.log('[DwspayService] elements.submit() succeeded - form validated and payment details collected');
        return { success: true };
      }
    } catch (error) {
      console.error('[DwspayService] Exception during elements.submit():', error);
      throw error;
    }
  }

  /**
   * Process payment - implements PaymentProvider.processPayment
   * Note: cardElement parameter is ignored for dwspay (uses Payment Element)
   */
  public async processPayment(clientSecret: string, amount: number, cardElement?: any): Promise<PaymentResult> {
    try {
      const result = await this.confirmPayment(clientSecret);
      
      if (result.error) {
        return {
          success: false,
          error: result.error.message || 'Payment failed'
        };
      }
      
      return {
        success: true,
        paymentIntentId: result.paymentIntent?.id
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'An unexpected error occurred during payment'
      };
    }
  }

  /**
   * Confirm payment with Payment Element
   * IMPORTANT: elements.submit() MUST be called before this method
   * See: https://stripe.com/docs/payments/accept-a-payment-deferred
   * @param clientSecret The payment intent client secret
   * @param returnUrlParams Optional parameters to include in return URL (e.g., reservation_id, is_reservation)
   */
  public async confirmPayment(clientSecret: string, returnUrlParams?: any): Promise<any> {
    console.log('[DwspayService] confirmPayment called with clientSecret:', (clientSecret && clientSecret.substring(0, 20)) + '...');
    
    if (!this.stripe) {
      console.log('[DwspayService] Stripe not initialized, initializing...');
      await this.initStripe();
    }

    if (!this.paymentElement) {
      console.error('[DwspayService] Payment Element not initialized');
      throw new Error('Payment Element not initialized. Call initPaymentElement() first.');
    }

    if (!this.elements) {
      console.error('[DwspayService] Elements not initialized');
      throw new Error('Stripe Elements not initialized. Call initPaymentElement() first.');
    }

    // Step 1: Submit elements form (validate and collect payment details)
    // This MUST be called before confirmPayment according to Stripe's deferred payment flow
    console.log('[DwspayService] Step 1: Submitting elements form...');
    const submitResult = await this.submitElements();
    
    if (submitResult.error) {
      console.error('[DwspayService] Elements submit failed, cannot proceed with confirmation');
      return { error: submitResult.error };
    }

    // Step 2: Confirm payment (must be called immediately after submit, no async operations in between)
    console.log('[DwspayService] Step 2: Calling stripe.confirmPayment...');
    // Construct return URL with existing query params and new ones
    let returnUrl = window.location.origin + window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);

    if (returnUrlParams) {
      Object.keys(returnUrlParams).forEach(key => {
        if (returnUrlParams[key] !== null && returnUrlParams[key] !== undefined) {
          searchParams.set(key, returnUrlParams[key]);
        }
      });
    }

    if (searchParams.toString()) {
      returnUrl += '?' + searchParams.toString();
    }

    console.log('[DwspayService] Step 2: Calling stripe.confirmPayment with return_url:', returnUrl);
    try {
      const result = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: clientSecret,
        confirmParams: {
          return_url: returnUrl,
        },
        redirect: 'if_required'
      });
      
      if (result.error) {
        console.error('[DwspayService] Stripe confirmPayment returned error:', result.error);
      } else {
        const status = (result.paymentIntent && result.paymentIntent.status) || 'unknown';
        console.log('[DwspayService] Stripe confirmPayment succeeded, payment status:', status);
        console.log('[DwspayService] PaymentIntent details:', {
          id: result.paymentIntent && result.paymentIntent.id,
          status: result.paymentIntent && result.paymentIntent.status,
          amount: result.paymentIntent && result.paymentIntent.amount
        });
      }
      
      return result;
    } catch (error) {
      console.error('[DwspayService] Exception during stripe.confirmPayment:', error);
      throw error;
    }
  }

  /**
   * Observable for Payment Element ready event
   */
  public onPaymentElementReady(): Observable<boolean> {
    return this.paymentElementReadySubject.asObservable();
  }

  /**
   * Observable for Payment Element change events (validation)
   */
  public onPaymentElementChange(): Observable<any> {
    return this.paymentElementChangeSubject.asObservable();
  }

  /**
   * Check if Payment Element is valid and ready
   */
  public isPaymentElementValidState(): boolean {
    return this.isPaymentElementValid;
  }

  /**
   * Check if Payment Element is initialized
   */
  public isPaymentElementInitialized(): boolean {
    return this.paymentElement !== null;
  }

  /**
   * Wait for Payment Element to be ready
   */
  public async waitForPaymentElementReady(maxWaitMs: number = 5000): Promise<boolean> {
    if (this.paymentElement) {
      return true;
    }

    return new Promise((resolve) => {
      const startTime = Date.now();
      const checkInterval = setInterval(() => {
        if (this.paymentElement) {
          clearInterval(checkInterval);
          resolve(true);
        } else if (Date.now() - startTime >= maxWaitMs) {
          clearInterval(checkInterval);
          resolve(false);
        }
      }, 100);
    });
  }

  /**
   * Unmount and destroy Payment Element
   */
  public destroyPaymentElement(): void {
    if (this.paymentElement) {
      try {
        this.paymentElement.unmount();
        this.paymentElement = null;
      } catch (error) {
        console.error('Error destroying payment element:', error);
      }
    }
    this.isPaymentElementValid = false;
  }

  /**
   * Check if DWSPay is enabled for a specific winery
   * @param wineryId The ID of the winery
   * @returns Observable with the dwspay status response
   */
  checkDwspayStatus(wineryId: string): Observable<{ enabled: boolean }> {
    return resource(`wsb2c://wspayments/accounts/winery/{wineryId}/dwspay`)
      .params({ wineryId })
      .get<{ enabled: boolean }>();
  }

  /**
   * Get payment methods available for a specific winery
   * @param wineryId The ID of the winery
   * @returns Observable with the payment methods response
   */
  getPaymentMethods(wineryId: string): Observable<{ payment_methods?: string[], paymentMethods?: string[] }> {
    return resource(`wsb2c://wspayments/accounts/winery/{wineryId}/payment-methods`)
      .params({ wineryId })
      .get<{ payment_methods?: string[], paymentMethods?: string[] }>();
  }

  /**
   * Inject Stripe.js into head if not already loaded
   */
  private injectIntoHead(): Promise<any> {
    if ((window as any)['Stripe']) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://js.stripe.com/v3/';
      script.async = true;
      script.onload = () => resolve(true);
      script.onerror = () => reject(new Error('Failed to load Stripe.js'));
      document.head.appendChild(script);
    });
  }
}
